import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, BarChart3, Car, Moon, Receipt, ShieldCheck, Sun, Wrench } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';
import { Field } from '@/ui/Field';
import { cn } from '@/lib/cn';
import { DESIGNS } from '@/theme/designs';
import { useTheme } from '@/theme/ThemeProvider';
import { BrandIcon } from '@/shell/BrandIcon';

const schema = z.object({
  company: z.string().min(1, 'Şirket seçiniz'),
  username: z.string().min(1, 'Kullanıcı adı giriniz').max(15),
  password: z.string().max(64),
  remember: z.boolean(),
});
type Form = z.infer<typeof schema>;

const FEATURES = [
  [Wrench, 'İş emri ve atölye takibi', 'Araç kabulden teslime tüm süreç'],
  [Receipt, 'Fatura, irsaliye, cari', 'Masaüstü programla aynı veritabanı'],
  [BarChart3, 'Raporlar ve pivot', 'BilsanRapor tanımları, PDF / Excel'],
  [ShieldCheck, 'Kullanıcı yetkileri', 'Menü, rapor ve kayıt bazında'],
] as const;

/** Delphi Sifre.pas giriş ekranı: şirket (Config\*.bls) + kullanıcı + şifre, "Hatırla" seçeneği */
export function LoginPage() {
  const { login } = useAuth();
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api<{ code: string; name: string; varsayilan?: boolean }[]>('/auth/companies') });
  const last = (() => {
    try { return JSON.parse(localStorage.getItem('dolphin.lastLogin') ?? 'null'); } catch { return null; }
  })();
  const { register, handleSubmit, setValue, formState } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { company: last?.company ?? '', username: last?.username ?? '', password: '', remember: !!last },
  });
  useEffect(() => {
    const list = companies.data ?? [];
    if (!list.length) return;
    // hatırlanan şirket listede yoksa (silinmiş/pasif) varsayılana dön
    if (!last?.company || !list.some((c) => c.code === last.company)) setValue('company', (list.find((c) => c.varsayilan) ?? list[0]).code);
  }, [companies.data]);

  const onSubmit = async (f: Form) => {
    setError(null);
    try {
      await login(f.company, f.username, f.password);
      if (!f.remember) localStorage.removeItem('dolphin.lastLogin');
    } catch (e: any) {
      setError(e.message);
    }
  };

  const d = theme.design.id;
  return (
    <div className="login grid min-h-full lg:grid-cols-[1.05fr_1fr]">
      {/* tanıtım paneli */}
      <aside className="login-art relative hidden overflow-hidden p-12 lg:flex lg:flex-col">
        <div className="login-art-bg pointer-events-none absolute inset-0" />
        <div className="relative flex items-center gap-3">
          <span className="brand-mark grid h-11 w-11 place-items-center"><BrandIcon size={22} /></span>
          <div><div className="login-art-title text-lg font-bold">Dolphin Oto Servis</div><div className="login-art-sub text-xs">Servis · Stok · Cari · Fatura</div></div>
        </div>
        <div className="relative mt-auto max-w-lg">
          <h1 className={cn('h-display login-art-title', d === 'atelier' ? 'text-[52px] leading-[1.02]' : 'text-[42px] leading-[1.08]')}>
            {d === 'pitstop' ? 'Atölyeniz, tek ekranda.' : d === 'obsidian' ? 'Hızlı. Net. Klavyeden.' : d === 'ledger' ? 'Servisin finansı, düzenli.' : d === 'atelier' ? 'Her araç, özenle.' : 'Servisinizi akıllıca yönetin.'}
          </h1>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {FEATURES.map(([I, t, s]) => (
              <div key={t} className="login-feat flex gap-3 p-3.5">
                <span className="login-feat-ic grid h-9 w-9 shrink-0 place-items-center"><I size={17} /></span>
                <div><div className="login-art-title text-[13px] font-semibold">{t}</div><div className="login-art-sub text-xs">{s}</div></div>
              </div>
            ))}
          </div>
        </div>
        {d === 'pitstop' && <Car className="pointer-events-none absolute -right-10 top-24 h-80 w-80 text-white/[.04]" strokeWidth={1} />}
      </aside>

      {/* form */}
      <main className="flex flex-col items-center justify-center px-5 py-10">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full max-w-[380px]">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="brand-mark grid h-10 w-10 place-items-center"><BrandIcon /></span>
            <div className="text-lg font-bold">Dolphin Oto Servis</div>
          </div>
          <h2 className="h-display text-[26px]">Oturum açın</h2>
          <p className="mt-1 text-sm muted">Şirketinizi seçip kullanıcı bilgilerinizle giriş yapın.</p>
          <div className="mt-7 flex flex-col gap-4">
            <Field label="Şirket" error={formState.errors.company?.message}>
              <select className="inp" {...register('company')}>
                {companies.data?.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Kullanıcı Adı" error={formState.errors.username?.message}>
              <input className="inp" autoComplete="username" autoFocus={!last?.username} {...register('username')} />
            </Field>
            <Field label="Şifre" error={formState.errors.password?.message}>
              <input className="inp" type="password" autoComplete="current-password" autoFocus={!!last?.username} {...register('password')} />
            </Field>
            <label className="flex items-center gap-2 text-[13px] muted">
              <input type="checkbox" className="accent-[var(--accent)]" {...register('remember')} /> Kullanıcı ve şirketi hatırla
            </label>
            {error && <div className="rounded border border-[var(--danger)]/40 bg-[var(--danger-soft)] px-3 py-2 text-[var(--danger-text)]">{error}</div>}
            <button className="btn btn-primary h-11 justify-center text-[14px]" disabled={formState.isSubmitting}>
              {formState.isSubmitting ? 'Giriş yapılıyor…' : <>Giriş <ArrowRight size={16} /></>}
            </button>
          </div>
        </form>
        <div className="mt-10 flex items-center gap-2" aria-label="Tasarım önizleme">
          {DESIGNS.map((x) => (
            <button key={x.id} type="button" title={`${x.ad} tasarımı`} onClick={() => theme.preview({ tasarim: x.id, ...(theme.prefs.mod === theme.design.dogalMod && x.dogalMod !== theme.prefs.mod ? { mod: x.dogalMod } : {}) })}
              className={cn('login-dot h-7 rounded-full px-2.5 text-[11px] font-medium', theme.prefs.tasarim === x.id && 'is-on')}>{x.ad}</button>
          ))}
          <button type="button" className="icon-btn grid h-7 w-7 place-items-center" title="Açık / koyu" onClick={() => theme.preview({ mod: theme.dark ? 'acik' : 'koyu' })}>{theme.dark ? <Sun size={14} /> : <Moon size={14} />}</button>
        </div>
      </main>
    </div>
  );
}
